import { Link as RouterLink } from 'react-router-dom';
import { Text, makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  container: {
    flex: '1 1 auto',
    padding: '40px 16px',
    maxWidth: '720px',
    margin: '0 auto',
    width: '100%',
    '@media (max-width: 480px)': {
      padding: '24px 12px',
    },
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: tokens.shadow16,
    wordWrap: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    '@media (max-width: 480px)': {
      padding: '24px 16px',
    },
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '24px',
    fontSize: '14px',
    color: tokens.colorBrandBackground,
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
  title: {
    display: 'block',
    fontSize: '28px',
    fontWeight: '600',
    marginBottom: '24px',
    color: tokens.colorNeutralForeground1,
  },
  updated: {
    display: 'block',
    fontSize: '13px',
    opacity: 0.6,
    marginBottom: '24px',
  },
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    display: 'block',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
    color: tokens.colorNeutralForeground1,
  },
  body: {
    fontSize: '14px',
    lineHeight: '1.7',
    color: tokens.colorNeutralForeground2,
  },
  footer: {
    padding: '20px 16px',
    textAlign: 'center' as const,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '6px',
    fontSize: '12px',
    lineHeight: '1.6',
    opacity: 0.6,
  },
  footerLink: {
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
});

export default function TermsOfService() {
  const styles = useStyles();

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.card}>
          <RouterLink to="/" className={styles.backLink}>← Back to Sign In</RouterLink>
          <Text className={styles.title}>Terms of Service</Text>
          <Text className={styles.updated}>Last updated: March 2026</Text>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>1. Acceptance of Terms</Text>
            <p className={styles.body}>
              By accessing or using the Sherpai platform operated by Component6 Pty Ltd, you agree to
              be bound by these Terms of Service. If you do not agree to all of these terms, you may
              not access or use the service.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>2. Description of Service</Text>
            <p className={styles.body}>
              Sherpai is an AI-powered assistant delivered through Microsoft Teams that helps
              organisations support their workforce through intelligent conversation, goal tracking,
              and knowledge management. The Admin Portal provides analytics, configuration, and
              solution library management capabilities.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>3. User Accounts</Text>
            <p className={styles.body}>
              Access to Sherpai requires authentication via your Microsoft organisational account.
              You are responsible for maintaining the confidentiality of your account credentials and
              for all activities that occur under your account. You must notify us immediately of any
              unauthorised use.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>4. Acceptable Use</Text>
            <p className={styles.body}>
              You agree to use Sherpai only for lawful purposes and in accordance with your
              organisation&apos;s policies. You shall not attempt to gain unauthorised access to any
              part of the service, interfere with or disrupt the service, or use the service to
              transmit harmful or malicious content.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>5. Intellectual Property</Text>
            <p className={styles.body}>
              The Sherpai platform, including its software, design, and content, is the intellectual
              property of Component6 Pty Ltd. Your use of the service does not grant you ownership of
              any intellectual property rights in the service or its content.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>6. Limitation of Liability</Text>
            <p className={styles.body}>
              To the maximum extent permitted by law, Component6 Pty Ltd shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages arising from your use
              of the service. Our total liability shall not exceed the amount paid by your
              organisation for the service in the preceding twelve months.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>7. Changes to Terms</Text>
            <p className={styles.body}>
              We reserve the right to modify these terms at any time. We will notify you of material
              changes by posting the updated terms on this page. Continued use of the service after
              changes constitutes acceptance of the modified terms.
            </p>
          </div>
        </div>
      </div>

      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <RouterLink to="/privacy" className={styles.footerLink}>Privacy Policy</RouterLink>
          <span>·</span>
          <RouterLink to="/terms" className={styles.footerLink}>Terms of Service</RouterLink>
          <span>·</span>
          <RouterLink to="/ai-disclosure" className={styles.footerLink}>AI Disclosure</RouterLink>
          <span>·</span>
          <span>© 2026 Component6 Pty Ltd</span>
        </div>
      </footer>
    </div>
  );
}
